import ArrowBackIcon from '@material-design-icons/svg/filled/arrow_back.svg?react';
import CancelIcon from '@material-design-icons/svg/filled/cancel.svg?react';
import InfoIcon from '@material-design-icons/svg/filled/info.svg?react';
import WarningIcon from '@material-design-icons/svg/filled/warning.svg?react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '../../lib/utils';

/**
 * Header, dialog and "needs attention" pieces shared by the admin detail pages
 * (dapp, publisher) so they read as one product with the tickets inspector.
 */

export const actionLinkClassName =
  'shrink-0 text-xs font-medium text-teal-300 transition-colors hover:text-teal-200';

export const headerIconButtonClassName =
  'inline-flex size-8 items-center justify-center rounded-lg border border-white/5 bg-white/[0.04] text-white/90 transition-colors hover:bg-white/[0.08] disabled:cursor-not-allowed disabled:opacity-40';

export const menuContentClassName =
  'w-52 rounded-lg border border-neutral-700 bg-neutral-900 p-1 text-white';

export const menuItemClassName =
  'cursor-pointer rounded text-sm text-neutral-200 focus:bg-neutral-800 focus:text-white [&>svg]:fill-current';

export function BackLink({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className="-ml-2 inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-xs text-neutral-400 transition-colors hover:bg-white/[0.04] hover:text-white"
    >
      <ArrowBackIcon aria-hidden="true" className="size-4 fill-current" />
      {children}
    </Link>
  );
}

export function MetaSeparator() {
  return (
    <span aria-hidden="true" className="text-neutral-700">
      ·
    </span>
  );
}

const HEADER_ACTION_TONES = {
  default: 'border-white/5 bg-white/[0.04] text-white/90 hover:bg-white/[0.08]',
  primary:
    'border-teal-400/30 bg-teal-500/15 text-teal-200 hover:border-teal-400/50 hover:bg-teal-500/25',
  caution:
    'border-orange-500/20 bg-orange-500/[0.06] text-orange-300 hover:border-orange-500/30 hover:bg-orange-500/[0.12]',
} as const;

export function HeaderAction({
  icon,
  onClick,
  disabled,
  tone = 'default',
  children,
}: {
  icon?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: keyof typeof HEADER_ACTION_TONES;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40',
        HEADER_ACTION_TONES[tone],
      )}
    >
      {icon}
      {children}
    </button>
  );
}

const DIALOG_ACTION_TONES = {
  default:
    'border-white/10 bg-white/[0.04] text-white/90 hover:bg-white/[0.08]',
  primary:
    'border-teal-400/30 bg-teal-500/15 text-teal-200 hover:border-teal-400/50 hover:bg-teal-500/25',
  caution:
    'border-orange-500/30 bg-orange-500/15 text-orange-200 hover:border-orange-500/50 hover:bg-orange-500/25',
} as const;

export function DialogAction({
  onClick,
  disabled,
  tone = 'default',
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  tone?: keyof typeof DIALOG_ACTION_TONES;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex h-9 items-center justify-center rounded-lg border px-4 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40',
        DIALOG_ACTION_TONES[tone],
      )}
    >
      {children}
    </button>
  );
}

/** One state tile in an overview's summary strip. */
export function Tile({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
      <div className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-neutral-500">
        {label}
      </div>
      <div className="mt-2 flex flex-col gap-1.5">{children}</div>
    </div>
  );
}

export type AttentionTone = 'error' | 'warning' | 'info';

export interface AttentionItem<TTab extends string = string> {
  id: string;
  tone: AttentionTone;
  title: string;
  detail?: string | null;
  /** Where the fix lives: another tab, an in-page anchor, or a route. */
  action?: { label: string; tab: TTab } | { label: string; href: string };
}

const ATTENTION_ORDER: Record<AttentionTone, number> = {
  error: 0,
  warning: 1,
  info: 2,
};

export function sortAttention<T extends AttentionItem<string>>(
  items: T[],
): T[] {
  return items.sort(
    (a, b) => ATTENTION_ORDER[a.tone] - ATTENTION_ORDER[b.tone],
  );
}

const ATTENTION_ICON: Record<
  AttentionTone,
  { Icon: typeof CancelIcon; className: string }
> = {
  error: { Icon: CancelIcon, className: 'text-red-300' },
  warning: { Icon: WarningIcon, className: 'text-amber-300' },
  info: { Icon: InfoIcon, className: 'text-sky-300' },
};

function AttentionAction<TTab extends string>({
  action,
  onTabChange,
}: {
  action: NonNullable<AttentionItem<TTab>['action']>;
  onTabChange: (tab: TTab) => void;
}) {
  if ('tab' in action) {
    return (
      <button
        type="button"
        onClick={() => onTabChange(action.tab)}
        className={actionLinkClassName}
      >
        {action.label} →
      </button>
    );
  }
  if (action.href.startsWith('#')) {
    return (
      <a href={action.href} className={actionLinkClassName}>
        {action.label} ↓
      </a>
    );
  }
  return (
    <Link to={action.href} className={actionLinkClassName}>
      {action.label} →
    </Link>
  );
}

/** Section trailing text: quiet when clear, amber when something needs a look. */
export function AttentionSummary({ count }: { count: number }) {
  return count === 0 ? (
    <span className="text-xs text-teal-400">All clear</span>
  ) : (
    <span className="text-xs text-amber-300">
      {count} {count === 1 ? 'item needs' : 'items need'} attention
    </span>
  );
}

export function AttentionList<TTab extends string>({
  items,
  onTabChange,
}: {
  items: AttentionItem<TTab>[];
  onTabChange: (tab: TTab) => void;
}) {
  if (items.length === 0) {
    return null;
  }

  return (
    <ul className="mt-4 divide-y divide-white/5 overflow-hidden rounded-xl border border-white/5">
      {items.map((item) => {
        const { Icon, className } = ATTENTION_ICON[item.tone];
        return (
          <li key={item.id} className="flex items-start gap-2.5 px-3 py-2.5">
            <Icon
              aria-hidden="true"
              className={cn('mt-0.5 size-4 shrink-0 fill-current', className)}
            />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white/90">{item.title}</div>
              {item.detail && (
                <div
                  className="mt-0.5 truncate text-xs text-neutral-400"
                  title={item.detail}
                >
                  {item.detail}
                </div>
              )}
            </div>
            {item.action && (
              <AttentionAction action={item.action} onTabChange={onTabChange} />
            )}
          </li>
        );
      })}
    </ul>
  );
}
